/**
 * AssignedHrSelect — picker bound to `/api/v1/master/hr-users`.
 *
 * Renders a Shadcn <Select> showing active admin / hr_manager / hr_recruiter
 * users, sorted by full_name. Used by:
 *   - Candidate Detail header (inline editor)
 *   - New Candidate wizard (Step 3)
 *
 * Behaviour
 * ---------
 * - Value is the user's UUID (`assigned_hr_id`).
 * - The dropdown shows "Full Name · Role" so the user understands who they
 *   are picking.
 * - When the current value points to a user no longer in the list (e.g., the
 *   HR was disabled), we still render their name as a stable label so we
 *   don't accidentally silently null the field.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { v1Get } from '@/lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const ROLE_LABEL = {
  admin: 'Admin',
  hr_manager: 'HR Manager',
  hr_recruiter: 'HR Recruiter',
};

export function useHrUsers() {
  return useQuery({
    queryKey: ['v1-master-hr-users'],
    queryFn: () => v1Get('/master/hr-users'),
    staleTime: 60_000,
  });
}

export function AssignedHrSelect({
  value,
  onChange,
  fallbackLabel = '',
  disabled = false,
  placeholder = 'Select HR owner',
  testId = 'assigned-hr-select',
}) {
  const { data: users = [] } = useHrUsers();

  // If the current value is set but not in the active HR list (e.g., the
  // assignee was disabled), inject a read-only fallback item so the trigger
  // doesn't show as empty/blank.
  const knownIds = new Set(users.map((u) => u.id));
  const stale = value && !knownIds.has(value) && fallbackLabel;

  return (
    <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {stale && (
          <SelectItem value={value} disabled data-testid={`${testId}-stale`}>
            {fallbackLabel} (no longer active)
          </SelectItem>
        )}
        {users.map((u) => (
          <SelectItem key={u.id} value={u.id} data-testid={`${testId}-option-${u.id}`}>
            <span className="font-medium">{u.full_name}</span>
            <span className="ml-2 text-xs text-muted-foreground">
              {ROLE_LABEL[u.role] || u.role}
            </span>
          </SelectItem>
        ))}
        {users.length === 0 && (
          <div className="px-3 py-2 text-xs text-muted-foreground">
            No active HR users found.
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
